import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useInvoiceData } from '@/hooks/useInvoiceData';
import { COMPANY_CONFIG } from '@/config/companyConfig';
import TopSection from '@/components/printPage/topsection/topsection';
import BottomSection from '@/components/printPage/bottomsection/bottomsection';
import '@/components/printPage/style.css';
import '@/components/printPage/font.css';
import '@/components/printPage/layout.css';

interface PrintInvoicePageProps {
  user: any;
  onLogout: () => void;
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

const PrintInvoicePage: React.FC<PrintInvoicePageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invoice, loading, error } = useInvoiceData(id);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/management');
  };

  if (loading) {
    return (
      <Box className="print-page-container">
        <Box className="print-controls">
          <CircularProgress />
          <Typography>Loading invoice...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box className="print-page-container">
        <Box className="print-controls">
          <Alert severity="error">
            {error || 'Invoice not found'}
          </Alert>
          <Button onClick={handleBack} startIcon={<BackIcon />}>
            Back to Management
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="print-page-container">
      {/* Print Controls - Hidden in print */}
      <Box className="print-controls print-no-print">
        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={<PrintIcon />}
          sx={{ mr: 2 }}
        >
          Print Invoice
        </Button>
        <Button
          onClick={handleBack}
          variant="outlined"
          startIcon={<BackIcon />}
        >
          Back to Management
        </Button>
      </Box>

      {/* A4 Container */}
      <Box className="print-a4-container">
        {/* Top Section - Header + Products */}
        <TopSection invoice={invoice} companyConfig={COMPANY_CONFIG} />
        
        {/* Bottom Section - Footer (Two Columns) */}
        <BottomSection invoice={invoice} companyConfig={COMPANY_CONFIG} />
      </Box>
    </Box>
  );
};

export default PrintInvoicePage;
